import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Check, Cookie, LockKeyhole, MapPinned, Settings2, ShieldCheck, X } from 'lucide-react';
import { consentNotice } from '../../shared/consent-notice.mjs';
import { site } from '../siteData.js';

const denied = { necessary: true, functional: false, analytics: false, marketing: false };
const copy = consentNotice.interfaceText;
export const openConsent = () => window.SalucciConsent.open();
export function useConsent() {
  return useSyncExternalStore(window.SalucciConsent.subscribe, window.SalucciConsent.getSnapshot);
}

export function ConsentManager({ path }) {
  const state = useConsent();
  const isPolicy = ['/cookie-policy', '/privacy-policy'].includes(path);
  const [open, setOpen] = useState(!state.hasChoice && !isPolicy);
  const [tab, setTab] = useState(0);
  const [editing, setEditing] = useState(!state.hasChoice);
  const [draft, setDraft] = useState(state.choices);
  const [confirmation, setConfirmation] = useState('');
  const dialog = useRef(null);
  const previousFocus = useRef(null);
  const hasOptional = ['functional', 'analytics', 'marketing'].some((key) => state.choices[key]);

  useEffect(() => {
    const show = () => {
      previousFocus.current = document.activeElement;
      setDraft(window.SalucciConsent.getSnapshot().choices);
      setTab(1);
      setEditing(false);
      setOpen(true);
    };
    window.addEventListener('salucci:open-consent', show);
    return () => window.removeEventListener('salucci:open-consent', show);
  }, []);

  useEffect(() => {
    if (!state.hasChoice) { setOpen(!isPolicy); setTab(0); setEditing(true); setDraft(denied); }
    else if (!editing) setDraft(state.choices);
  }, [state, isPolicy]);

  const complete = (action, choices) => {
    window.SalucciConsent.choose(action, choices);
    setOpen(false);
    setConfirmation(action === 'revoke' ? 'Consenso revocato. Mappa e video esterni sono disattivati.' : action === 'close_x' ? 'Prosegui con i soli strumenti necessari.' : 'Preferenze cookie salvate.');
  };

  useEffect(() => {
    if (!open) return;
    if (!previousFocus.current) previousFocus.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.current?.focus();
    const handleKeys = (event) => {
      if (event.key === 'Escape') { event.preventDefault(); complete('close_x', denied); }
      if (event.key !== 'Tab') return;
      const focusable = [...dialog.current.querySelectorAll('button:not(:disabled), a[href], input:not(:disabled), [tabindex="0"]')];
      const first = focusable[0];
      const last = focusable.at(-1);
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialog.current)) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener('keydown', handleKeys);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeys);
      previousFocus.current?.focus?.();
      previousFocus.current = null;
    };
  }, [open]);

  useEffect(() => {
    if (!confirmation) return;
    const timeout = setTimeout(() => setConfirmation(''), 6500);
    return () => clearTimeout(timeout);
  }, [confirmation]);

  function selectTab(index, focus = false) {
    setTab(index);
    if (focus) dialog.current?.querySelector(`#consent-tab-${index}`)?.focus();
  }

  return (
    <>
      {(state.hasChoice || isPolicy) && (
        <button className="consent-widget" onClick={openConsent} aria-label="Apri preferenze cookie" title="Preferenze cookie">
          <Cookie size={23} strokeWidth={1.8} aria-hidden="true" />
        </button>
      )}
      {open && (
        <div className="consent-backdrop">
          <section className="consent-dialog" role="dialog" aria-modal="true" aria-labelledby="consent-title" aria-describedby="consent-description" tabIndex={-1} ref={dialog}>
            <header className="consent-header">
              <div className="consent-brand"><img src={site.logo} alt="" width="46" height="46" /><div><strong>Studio Legale Salucci</strong><span>Preferenze sulla privacy</span></div></div>
              <button className="consent-close" onClick={() => complete('close_x', denied)} aria-label="Chiudi e prosegui con i soli cookie necessari" title="Prosegui con i soli necessari"><X size={20} /></button>
            </header>
            <div className="consent-tabs" role="tablist" aria-label="Gestione cookie">
              {copy.tabs.map((label, index) => (
                <button key={label} id={`consent-tab-${index}`} role="tab" aria-selected={tab === index} aria-controls={`consent-panel-${index}`} tabIndex={tab === index ? 0 : -1} onClick={() => selectTab(index)} onKeyDown={(event) => {
                  if (['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) {
                    event.preventDefault();
                    selectTab(event.key === 'Home' ? 0 : event.key === 'End' ? 2 : (index + (event.key === 'ArrowRight' ? 1 : 2)) % 3, true);
                  }
                }}>{label}</button>
              ))}
            </div>
            <div className="consent-content">
              <h2 id="consent-title">{consentNotice.title}</h2>
              <p id="consent-description">{consentNotice.overview}</p>
              <div id={`consent-panel-${tab}`} role="tabpanel" aria-labelledby={`consent-tab-${tab}`} tabIndex={0}>
                {tab === 0 && (
                  <>
                    <div className="consent-overview-note"><ShieldCheck size={21} aria-hidden="true" /><p>{copy.overviewNote}</p></div>
                    <p className="consent-close-note">{consentNotice.closeExplanation}</p>
                  </>
                )}
                {tab === 1 && (
                  <div className="consent-categories">
                    {consentNotice.categories.map((category) => {
                      const enabled = editing ? draft[category.key] : state.choices[category.key];
                      return (
                        <div className="consent-category" key={category.key}>
                          <div><h3 id={`consent-label-${category.key}`}>{category.label}{!category.optional && <LockKeyhole size={14} aria-label="Sempre attivi" />}</h3><p id={`consent-help-${category.key}`}>{category.description}</p></div>
                          {editing && category.optional ? (
                            <button type="button" className="consent-switch" role="switch" aria-checked={enabled} aria-labelledby={`consent-label-${category.key}`} aria-describedby={`consent-help-${category.key}`} onClick={() => setDraft((current) => ({ ...current, [category.key]: !current[category.key] }))}><span /></button>
                          ) : (
                            <span className={`consent-status ${enabled ? 'is-active' : ''}`}>{category.optional ? enabled ? copy.status.active : copy.status.inactive : copy.status.necessary}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {tab === 2 && (
                  <div className="consent-information">
                    <h3>Cosa viene memorizzato</h3><p>{copy.storageInformation}</p>
                    <h3>Servizi esterni</h3><ul>{consentNotice.services.map((service) => <li key={service.name}><strong>{service.name}</strong><span>{service.purpose}</span><a href={service.policy} target="_blank" rel="noopener noreferrer">Informativa {service.vendor}</a></li>)}</ul>
                    <p>{copy.inactiveServices}</p>
                  </div>
                )}
              </div>
              <div className="consent-policy-links"><a href="/cookie-policy">Cookie policy</a><a href="/privacy-policy">Privacy policy</a></div>
            </div>
            <footer className="consent-actions">
              {state.hasChoice && !editing ? (
                <><button className="consent-button consent-button-secondary" disabled={!hasOptional} onClick={() => complete('revoke', denied)}>{hasOptional ? copy.revoke : copy.revoked}</button><button className="consent-button consent-button-primary" onClick={() => { setDraft(state.choices); setTab(1); setEditing(true); }}><Settings2 size={17} />{copy.modify}</button></>
              ) : (
                <><button className={`consent-button ${tab === 1 ? 'consent-button-secondary' : 'consent-button-primary'}`} onClick={() => complete('accept_all', { necessary: true, functional: true, analytics: true, marketing: true })}>{copy.acceptAll}</button><button className={`consent-button ${tab === 1 ? 'consent-button-primary' : 'consent-button-secondary'}`} onClick={() => tab === 1 ? complete('custom', draft) : (setTab(1), setEditing(true))}>{tab === 1 ? <Check size={17} /> : <Settings2 size={17} />}{tab === 1 ? copy.save : copy.customize}</button></>
              )}
            </footer>
          </section>
        </div>
      )}
      <div className="consent-announcement" role="status" aria-live="polite" aria-atomic="true">{confirmation && <div><ShieldCheck size={20} /><span>{confirmation}</span><button onClick={() => setConfirmation('')} aria-label="Chiudi conferma"><X size={17} /></button></div>}</div>
    </>
  );
}

export function ConsentPlaceholder({ title, children, className = '' }) {
  return <div className={`consent-placeholder ${className}`}><MapPinned size={30} aria-hidden="true" /><h3>{title}</h3><p>{children || 'Per visualizzare questo contenuto esterno autorizza la categoria Funzionali nelle preferenze cookie.'}</p><button className="consent-button consent-button-primary" onClick={openConsent}><Settings2 size={17} />Preferenze cookie</button></div>;
}

export function ConsentedArticleContent({ html }) {
  const { choices } = useConsent();
  const template = document.createElement('template');
  template.innerHTML = html;
  template.content.querySelectorAll('iframe, img').forEach((element) => {
    const src = element.getAttribute('src');
    if (!src) return;
    let url;
    try { url = new URL(src, window.location.origin); } catch { element.remove(); return; }
    if (element.tagName === 'IMG' && url.origin === window.location.origin) return;
    const allowed = element.tagName === 'IMG' ? url.protocol === 'https:' : url.protocol === 'https:' && ['www.youtube.com', 'youtube.com', 'www.youtube-nocookie.com', 'player.vimeo.com'].includes(url.hostname);
    if (!allowed) { element.remove(); return; }
    if (choices.functional) { element.setAttribute('referrerpolicy', 'no-referrer'); return; }
    const placeholder = document.createElement('div');
    placeholder.className = 'consent-placeholder';
    const title = document.createElement('h3');
    title.textContent = element.tagName === 'IFRAME' ? 'Video esterno disattivato' : 'Immagine esterna disattivata';
    const text = document.createElement('p');
    text.textContent = 'Autorizza la categoria Funzionali per visualizzare questo contenuto.';
    const button = document.createElement('button');
    button.className = 'consent-button consent-button-primary';
    button.setAttribute('data-consent-preferences', '');
    button.textContent = 'Preferenze cookie';
    placeholder.append(title, text, button);
    element.replaceWith(placeholder);
  });
  return <div onClick={(event) => { if (event.target.closest('[data-consent-preferences]')) openConsent(); }} dangerouslySetInnerHTML={{ __html: template.innerHTML }} />;
}

export function PolicyPage({ kind }) {
  const [retention, setRetention] = useState(undefined);
  useEffect(() => {
    const controller = new AbortController();
    fetch('/api/consent/config', { signal: controller.signal }).then((response) => response.ok ? response.json() : null).then((data) => setRetention(data?.evidence_retention_days || null)).catch(() => {});
    return () => controller.abort();
  }, []);
  return <main className="consent-policy-page"><div><p className="text-sm font-bold text-brass">Studio Legale Salucci</p><h1>{kind === 'cookie' ? 'Cookie policy' : 'Privacy policy'}</h1><p>Versione {consentNotice.version} · Aggiornamento 6 ottobre 2026</p>{consentNotice.policies[kind].map((section) => <section key={section.title}><h2>{section.title}</h2><p>{section.text}</p>{['Archivio delle scelte', 'Documentazione del consenso'].includes(section.title) && retention === null && <p>{copy.archiveUnavailable}</p>}{section.title === 'Conservazione' && retention && <p>Gli eventi nell’archivio sono conservati per {retention} giorni dalla ricezione.</p>}</section>)}<button className="consent-button consent-button-primary" onClick={openConsent}><Settings2 size={17} />Gestisci preferenze cookie</button></div></main>;
}
